// The scrollable conversation thread. Renders user + assistant turns, the
// collapsing tool trace, streamed markdown-lite text, confirmation cards, and
// per-turn error states. Pure view over useAssistant state.

import { MarkdownLite } from './MarkdownLite.tsx';
import { ConfirmationCard } from './ConfirmationCard.tsx';
import { CheckMark, UndoMark } from '../components/StatusMarks.tsx';
import type { ChatTurn, AssistantTurn, ToolLine, AppliedActionState } from './types.ts';

/** An auto-applied reversible edit: a "done" line with a one-click Undo (docs/05 move #3). */
function AppliedNote({
  action,
  onUndo,
}: {
  action: AppliedActionState;
  onUndo: (batchId: string) => void;
}) {
  const undone = action.status === 'undone';
  return (
    <div className={`asst-applied${undone ? ' undone' : ''}`} role="status">
      <span className="asst-applied-icon" aria-hidden>
        {undone ? <UndoMark /> : <CheckMark />}
      </span>
      <span className="asst-applied-summary">
        {undone ? `Undone — ${action.summary}` : action.summary}
      </span>
      {action.undo_available && !undone && (
        <button
          type="button"
          className="btn ghost sm asst-undo-btn"
          onClick={() => onUndo(action.batch_id)}
          disabled={action.status === 'undoing'}
        >
          {action.status === 'undoing' ? 'Undoing…' : 'Undo'}
        </button>
      )}
      {action.status === 'undo_failed' && (
        <span className="asst-applied-error">{action.error ?? 'Undo failed'}</span>
      )}
    </div>
  );
}

function ToolTrace({ tools, streaming }: { tools: ToolLine[]; streaming: boolean }) {
  if (tools.length === 0) return null;
  const allDone = tools.every((t) => t.done);

  // While work is in flight, show live lines. Once everything's done we collapse
  // the trace into a native <details> so the finished steps stay available but
  // out of the way (docs/05 "compact status lines… collapse when done").
  if (allDone && !streaming) {
    return (
      <details className="asst-tools asst-tools-done">
        <summary>
          {tools.length === 1 ? '1 step' : `${tools.length} steps`} · done
        </summary>
        {tools.map((t, i) => (
          <div key={i} className="asst-tool-line done">
            <span aria-hidden><CheckMark /></span> {t.summary}
          </div>
        ))}
      </details>
    );
  }

  return (
    <div className="asst-tools" aria-live="polite">
      {tools.map((t, i) => (
        <div key={i} className={`asst-tool-line${t.done ? ' done' : ''}`}>
          <span aria-hidden>{t.done ? <CheckMark /> : '…'}</span> {t.summary}
        </div>
      ))}
    </div>
  );
}

function AssistantMessage({
  turn,
  onConfirm,
  onUndo,
}: {
  turn: AssistantTurn;
  onConfirm: (batchId: string, approve: boolean) => void;
  onUndo: (batchId: string) => void;
}) {
  const empty =
    !turn.text &&
    turn.tools.length === 0 &&
    turn.batches.length === 0 &&
    turn.applied.length === 0 &&
    !turn.error;
  return (
    <div className="asst-msg asst-msg-assistant">
      <ToolTrace tools={turn.tools} streaming={turn.streaming} />
      {turn.text && (
        <div className="asst-prose">
          <MarkdownLite text={turn.text} />
        </div>
      )}
      {turn.applied.map((a) => (
        <AppliedNote key={a.batch_id} action={a} onUndo={onUndo} />
      ))}
      {turn.batches.map((b) => (
        <ConfirmationCard key={b.batch_id} batch={b} onConfirm={onConfirm} />
      ))}
      {turn.error && <div className="asst-error">{turn.error}</div>}
      {empty && turn.streaming && (
        <div className="asst-typing" aria-label="Assistant is thinking">
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
  );
}

export function Thread({
  turns,
  streamError,
  onConfirm,
  onUndo,
  onRetry,
}: {
  turns: ChatTurn[];
  streamError: string | null;
  onConfirm: (batchId: string, approve: boolean) => void;
  onUndo: (batchId: string) => void;
  onRetry: () => void;
}) {
  return (
    <div className="asst-thread">
      {turns.map((turn) =>
        turn.role === 'user' ? (
          <div key={turn.id} className="asst-msg asst-msg-user">
            {turn.attachments.length > 0 && (
              <div className="asst-msg-attachments">
                {turn.attachments.map((a) => (
                  <span key={a.id} className="chip">
                    {a.name}
                  </span>
                ))}
              </div>
            )}
            <div className="asst-bubble">{turn.text}</div>
          </div>
        ) : (
          <AssistantMessage key={turn.id} turn={turn} onConfirm={onConfirm} onUndo={onUndo} />
        ),
      )}
      {streamError && (
        <div className="asst-stream-error" role="alert">
          <span>{streamError}</span>
          <button className="btn ghost sm" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
